/**
 * @fileoverview Per-tab validation for the template editor / run wizard.
 *               `validateTab` feeds `tabErrors(...)` which surfaces as the
 *               `errors` prop on each step panel and gates Next/save.
 * @module import-editor/utils/templateValidation
 *
 * Responsibilities:
 * - Validate each wizard tab (basics, mappings, matchKey, geocoding, review).
 * - `validateAiTransformMapping` mirrors the backend AI-transform config
 *   contract (instruction, output fields/count, nonblank output targets,
 *   ownership.* scoping in parcelOwnerUpdate) so malformed AI mappings are
 *   caught at authoring time.
 *
 * Dependencies:
 * - @firesquirrel/local-db types (FieldMapping, ImportDataMode).
 *
 * Architecture:
 * - Pure functions only — no API calls, no Dexie, no React.
 */
import type { TemplateEditorState, TemplateEditorTab } from '../templateEditor/types';
import type { FieldMapping, ImportDataMode } from '@firesquirrel/local-db';

export interface TabValidationResult {
  valid: boolean;
  errors: string[];
}

const AI_TRANSFORM_TYPES = new Set(['aiParse', 'aiSplit', 'aiClassify']);

/**
 * Validate a single mapping's AI-transform configuration. Returns human-
 * readable error strings (empty when valid). Mirrors the backend
 * `validateAiTransformMappingScope` contract so the wizard catches malformed
 * AI mappings before save/dry-run instead of at pipeline time:
 * - aiParse: instruction nonblank; outputFields nonempty; aiOutputMappings
 *   exactly one nonblank target per output field.
 * - aiSplit: instruction nonblank; maxOutputs >= 1; at least maxOutputs
 *   nonblank aiOutputMappings targets.
 * - aiClassify: legacy shape accepted in general mode.
 * - parcelOwnerUpdate: every AI output target must be ownership.*;
 *   aiClassify's primary targetField must be ownership.* too.
 */
export function validateAiTransformMapping(
  mapping: FieldMapping,
  dataMode?: ImportDataMode | string,
): string[] {
  const errors: string[] = [];
  const transform = mapping.transform;
  if (!transform?.type || !AI_TRANSFORM_TYPES.has(transform.type)) return errors;

  const isOwnerUpdate = dataMode === 'parcelOwnerUpdate';
  const label = `"${mapping.targetField}" (${transform.type})`;

  const checkTarget = (target: string | undefined, description: string): void => {
    const path = (target ?? '').trim();
    if (!path) {
      errors.push(`${label}: ${description} requires a nonblank output target.`);
      return;
    }
    if (isOwnerUpdate && !path.startsWith('ownership.')) {
      errors.push(`${label}: ${description} "${path}" must be an ownership.* field for parcel owner updates.`);
    }
  };

  if (transform.type === 'aiParse') {
    if (!(transform.instruction ?? '').trim()) {
      errors.push(`${label}: an instruction is required.`);
    }
    const outputFields = transform.outputFields ?? [];
    if (outputFields.length === 0) {
      errors.push(`${label}: at least one output field is required.`);
    }
    const mappings = mapping.aiOutputMappings ?? [];
    if (outputFields.length > 0 && mappings.length !== outputFields.length) {
      errors.push(`${label}: exactly ${outputFields.length} output target(s) required — one per output field.`);
    }
    outputFields.forEach((fieldName, i) => {
      checkTarget(mappings[i], `output target for "${fieldName}"`);
    });
  } else if (transform.type === 'aiSplit') {
    if (!(transform.instruction ?? '').trim()) {
      errors.push(`${label}: an instruction is required.`);
    }
    const maxOutputs = transform.maxOutputs ?? 0;
    if (maxOutputs < 1) {
      errors.push(`${label}: max outputs must be at least 1.`);
    } else {
      const mappings = mapping.aiOutputMappings ?? [];
      if (mappings.length < maxOutputs) {
        errors.push(`${label}: at least ${maxOutputs} output target(s) required — one per split output.`);
      }
      for (let i = 0; i < Math.min(maxOutputs, mappings.length); i++) {
        checkTarget(mappings[i], `output target for split index ${i}`);
      }
    }
  } else if (transform.type === 'aiClassify') {
    if (isOwnerUpdate) {
      checkTarget(mapping.targetField, 'primary target');
    }
    const parsePerCategory = transform.parsePerCategory;
    if (parsePerCategory && Object.keys(parsePerCategory).length > 0) {
      const required = Math.max(
        ...Object.values(parsePerCategory).map((cfg) => cfg?.outputFields?.length ?? 0),
      );
      const mappings = mapping.aiOutputMappings ?? [];
      if (mappings.length < required) {
        errors.push(`${label}: at least ${required} parsed output target(s) required for per-category parsing.`);
      }
      for (let i = 0; i < Math.min(required, mappings.length); i++) {
        checkTarget(mappings[i], `parsed output target at index ${i}`);
      }
    }
  }

  return errors;
}

export interface AllTabsValidationResult {
  isValid: boolean;
  errorTabs: TemplateEditorTab[];
  allErrors: string[];
}

// ---------------------------------------------------------------------------
// Per-tab validation
// ---------------------------------------------------------------------------

export function validateTab(tab: TemplateEditorTab, state: TemplateEditorState): TabValidationResult {
  const errors: string[] = [];
  const effectiveMappings = (state.fieldMappings ?? []).filter((m) => {
    const hasTargetField = !!m.targetField?.trim();
    const hasSourceColumn = !!m.sourceColumns?.[0]?.trim();
    const hasDefaultValue = !!m.defaultValue?.trim();
    const hasMatchKey = !!m.isMatchKey;
    const hasTransform = !!m.transform?.type && m.transform.type !== 'none';
    return hasSourceColumn || hasDefaultValue || hasMatchKey || hasTransform || !hasTargetField;
  });

  if (tab === 'basics') {
    if (!state.name.trim()) errors.push('Template name is required.');
    if (state.name.trim().length > 200) errors.push('Template name must be 200 characters or fewer.');
    if (!state.dataMode) errors.push('Data mode is required.');
    if (!state.importBehavior) errors.push('Import behavior is required.');
  }

  if (tab === 'sampleData') {
    // Sample data is informational — authors can skip uploading a file
    // and define mappings by hand (e.g. copying from another template).
    // The downstream 'mappings' tab still enforces that every mapping has
    // a real source column when a file IS uploaded. Keeping this always-
    // valid avoids blocking navigation for authors who know their field
    // list from memory.
    return { valid: true, errors: [] };
  }

  if (tab === 'mappings') {
    // Shell-mode templates defer mapping to import time, so the mappings
    // tab is not shown and not validated. The import wizard enforces
    // mapping completeness when a shell-based import runs.
    if (state.mode === 'shell') {
      return { valid: true, errors: [] };
    }
    if (effectiveMappings.length === 0) {
      errors.push('At least one field mapping is required.');
    } else {
      effectiveMappings.forEach((m, i) => {
        if (!m.targetField.trim()) errors.push(`Mapping #${i + 1}: target field is required.`);
        const hasSourceColumn = !!m.sourceColumns?.[0]?.trim();
        const hasDefaultValue = !!m.defaultValue?.trim();
        if (!hasSourceColumn && !hasDefaultValue) {
          errors.push(`Mapping #${i + 1}: source column is required.`);
        }
        // AI transform config (instruction / output fields / output targets,
        // incl. ownership.* scoping in parcelOwnerUpdate) — same contract as
        // the backend validator, surfaced here via the existing errors flow.
        for (const aiError of validateAiTransformMapping(m, state.dataMode)) {
          errors.push(`Mapping #${i + 1}: ${aiError}`);
        }
      });
      if (state.dataMode === 'parcel') {
        const hasParcelNumber = effectiveMappings.some(m => m.targetField === 'parcelNumber');
        if (!hasParcelNumber) errors.push('Parcel imports require a mapping for "parcelNumber".');
      }
      if (state.dataMode === 'lookup') {
        const hasCode = effectiveMappings.some(m => m.targetField === 'code');
        const hasName = effectiveMappings.some(m => m.targetField === 'name');
        if (!hasCode) errors.push('Lookup imports require a mapping for "code".');
        if (!hasName) errors.push('Lookup imports require a mapping for "name".');
      }
      if (state.dataMode === 'contact') {
        const hasName = effectiveMappings.some(m => m.targetField === 'name');
        if (!hasName) errors.push('Contact imports require a mapping for "name".');
      }
      if (state.importBehavior === 'upsert' || state.importBehavior === 'full-sync') {
        const hasMatchKey = effectiveMappings.some(m => m.isMatchKey);
        if (!hasMatchKey) errors.push('Upsert/Full-Sync behavior requires at least one field marked as a match key.');
      }
    }
  }

  if (tab === 'matchKey') {
    if (state.mode === 'shell') {
      // Shell mode: the author declares the *target field name* they'll be
      // matching on at import time (e.g. 'parcelNumber'). There are no
      // mappings yet, so we just require the field name when the behavior
      // depends on it.
      if (state.importBehavior === 'upsert' || state.importBehavior === 'full-sync') {
        if (!state.matchKeyField.trim()) {
          errors.push('Match key target field is required for upsert or full-sync.');
        }
      }
    } else if (state.importBehavior === 'upsert' || state.importBehavior === 'full-sync') {
      const hasMatchKey = effectiveMappings.some(m => m.isMatchKey);
      if (!hasMatchKey) {
        errors.push('At least one field must be designated as a match key for upsert or full-sync.');
      }
      if (state.matchKeyField && !effectiveMappings.some(m => m.targetField === state.matchKeyField && m.isMatchKey)) {
        errors.push('The selected match key field must be marked as a match key in Field Mappings.');
      }
    }
  }

  if (tab === 'geocoding') {
    // Item 2 (2026-05-08): geocoding does not apply to parcel/lookup
    // imports. The tab is hidden for those modes via
    // getVisibleEditorTabs(); validation here short-circuits to "valid"
    // so a stale `geocodingEnabled: true` flag carried over from a mode
    // change cannot block save-as-publish. The save path's
    // stateToTemplate clears the flag on persistence.
    if (state.dataMode === 'parcel' || state.dataMode === 'lookup') {
      return { valid: true, errors: [] };
    }
    if (state.geocodingEnabled) {
      if (!state.geocodingAddressFields || state.geocodingAddressFields.length === 0) {
        errors.push('Geocoding requires at least one address field to be specified.');
      } else if (state.mode !== 'shell') {
        // In shell mode the address fields refer to target-field names that
        // will be mapped at import time; we cannot cross-check them against
        // field mappings that don't exist yet.
        state.geocodingAddressFields.forEach(f => {
          const inMappings = effectiveMappings.some(m => m.targetField === f);
          if (!inMappings) {
            errors.push(`Geocoding address field "${f}" is not mapped in Field Mappings.`);
          }
        });
      }
    }
  }

  if (tab === 'review') {
    // Shell mode skips the 'mappings' tab; everything else is the same.
    const tabs: TemplateEditorTab[] = state.mode === 'shell'
      ? ['basics', 'matchKey', 'geocoding']
      : ['basics', 'mappings', 'matchKey', 'geocoding'];
    const childErrors = tabs.flatMap(t => validateTab(t, state).errors);
    if (childErrors.length > 0) {
      errors.push('One or more tabs have validation errors. Please fix them before saving.');
    }
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// Validate all tabs at once (used on final save)
// ---------------------------------------------------------------------------

export function validateAllTabs(state: TemplateEditorState): AllTabsValidationResult {
  // Shell mode skips the 'mappings' tab entirely.
  const tabs: TemplateEditorTab[] = state.mode === 'shell'
    ? ['basics', 'matchKey', 'geocoding']
    : ['basics', 'mappings', 'matchKey', 'geocoding'];
  const errorTabs: TemplateEditorTab[] = [];
  const allErrors: string[] = [];

  for (const tab of tabs) {
    const result = validateTab(tab, state);
    if (!result.valid) {
      errorTabs.push(tab);
      allErrors.push(...result.errors);
    }
  }

  return { isValid: errorTabs.length === 0, errorTabs, allErrors };
}

// ---------------------------------------------------------------------------
// Badge helper: ✓ / ! / ● / ○
// ---------------------------------------------------------------------------

export type TabBadge = 'valid' | 'error' | 'visited' | 'unvisited';

export function getTabBadge(
  tab: TemplateEditorTab,
  state: TemplateEditorState,
): TabBadge {
  if (!state.visitedTabs.has(tab)) return 'unvisited';
  const result = validateTab(tab, state);
  return result.valid ? 'valid' : 'error';
}
